'use client';

import { RadixTooltipContent } from '../../../../components/ui';
import { cn } from '../../../../lib/utils';
import { ToolButton } from '../../../primitives/buttons';
import { Slider } from '../../../primitives/controls';
import {
  FastForwardFill,
  FastRewindFill,
  PlayCircleFill,
  SkipNextFill,
  SkipPreviousFill,
  VolumeOff,
  VolumeUp,
  PauseFill,
  Repeat,
  Fullscreen,
  FullscreenExit,
  VolumeDown,
} from '@crabs-ai/web-icons';
import { useState } from 'react';

export interface PlayerTooltip {
  node?: string | React.ReactNode;
  subNode?: string | React.ReactNode;
}

export interface PlayerProps extends Omit<
  React.ComponentProps<'div'>,
  'onVolumeChange'
> {
  /** 시간 정보 */
  time: {
    start: number;
    end: number;
  };
  /** 종료 시간 텍스트 */
  endTimeText?: string;
  /** 현재 재생 시간 */
  currentTime?: number;
  /** 현재 재생 시간 텍스트 */
  currentTimeText?: string;
  /** 볼륨 */
  volume?: number;
  /** 재생 상태 */
  isPlaying?: boolean;
  /** 전체 화면 상태 */
  isFullscreen?: boolean;
  /** 반복 상태 */
  isShowRepeat?: boolean;
  /** 전체 화면 상태 */
  isShowFullscreen?: boolean;
  /** 오디오 컴포넌트 속성 */
  audioProps?: React.ComponentProps<'div'>;
  /** 볼륨 툴팁 텍스트 */
  volumeTooltip?: PlayerTooltip;
  /** 재생/정지 툴팁 텍스트 */
  playAndPauseTooltip?: PlayerTooltip;
  /** 일시정지 툴팁 텍스트 */
  pauseTooltip?: PlayerTooltip;
  /** 빠른 전진 툴팁 텍스트 */
  fastForwardTooltip?: PlayerTooltip;
  /** 빠른 되감기 툴팁 텍스트 */
  fastRewindTooltip?: PlayerTooltip;
  /** 이전 툴팁 텍스트 */
  skipPreviousTooltip?: PlayerTooltip;
  /** 다음 툴팁 텍스트 */
  skipNextTooltip?: PlayerTooltip;
  /** 전체 화면 툴팁 텍스트 */
  fullscreenTooltip?: PlayerTooltip;
  /** 반복 툴팁 텍스트 */
  repeatTooltip?: PlayerTooltip;
  /** 이전 버튼 비활성화 상태 */
  isSkipPreviousDisabled?: boolean;
  /** 이전 버튼 노출 여부 */
  isVisibleSkipPrevious?: boolean;
  /** 빠른 되감기 버튼 비활성화 상태 */
  isFastRewindDisabled?: boolean;
  /** 재생/일시정지 버튼 비활성화 상태 */
  isPlayDisabled?: boolean;
  /** 빠른 전진 버튼 비활성화 상태 */
  isFastForwardDisabled?: boolean;
  /** 다음 버튼 비활성화 상태 */
  isSkipNextDisabled?: boolean;
  /** 빠른 되감기 버튼 노출 여부 */
  isVisibleFastRewind?: boolean;
  /** 빠른 전진 버튼 노출 여부 */
  isVisibleFastForward?: boolean;
  /** 다음 버튼 노출 여부 */
  isVisibleSkipNext?: boolean;
  /** 볼륨 토글 이벤트 */
  onToggleVolume?: () => void;
  /** 볼륨 변경 이벤트 */
  onVolumeChange?: (volume: number) => void;
  /** 재생 이벤트 */
  onPlay?: () => void;
  /** 일시정지 이벤트 */
  onPause?: () => void;
  /** 시간 변경 이벤트 */
  onTimeChange?: (time: number) => void;
  /** 빠른 전진 이벤트 */
  onFastForward?: () => void;
  /** 빠른 되감기 이벤트 */
  onFastRewind?: () => void;
  /** 이전 곡 이벤트 */
  onSkipPrevious?: () => void;
  /** 다음 곡 이벤트 */
  onSkipNext?: () => void;
  /** 반복 이벤트 */
  onRepeat?: () => void;
  /** 전체 화면 이벤트 */
  onFullscreen?: () => void;
  /** Slider Step */
  step?: number;
  /** 툴팁 포탈 속성 */
  portalProps?: React.ComponentProps<typeof RadixTooltipContent>['portalProps'];
}

// 초 단위 시간을 시:분:초 또는 분:초 형식으로 변환하는 함수
function formatTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    // 1시간 이상일 때: h:mm:ss 형식
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  } else {
    // 1시간 미만일 때: mm:ss 형식
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
}

function Player({
  time,
  endTimeText: externalEndTimeText,
  currentTime: externalCurrentTime,
  isPlaying: externalIsPlaying,
  currentTimeText: externalCurrentTimeText,
  volume: externalVolume,
  isFullscreen: externalIsFullscreen,
  isShowRepeat: externalIsShowRepeat = true,
  isShowFullscreen: externalIsShowFullscreen = true,
  onVolumeChange: onExternalVolumeChange,
  onToggleVolume,
  audioProps,
  className,
  onTimeChange,
  onPlay,
  onPause,
  onFastForward,
  onFastRewind,
  onSkipPrevious,
  onSkipNext,
  onRepeat,
  onFullscreen,
  step = 1,
  playAndPauseTooltip,
  volumeTooltip,
  fastForwardTooltip,
  fastRewindTooltip,
  skipPreviousTooltip,
  skipNextTooltip,
  fullscreenTooltip,
  repeatTooltip,
  isSkipPreviousDisabled = false,
  isFastRewindDisabled = false,
  isPlayDisabled = false,
  isFastForwardDisabled = false,
  isSkipNextDisabled = false,
  isVisibleSkipPrevious = true,
  isVisibleFastRewind = true,
  isVisibleFastForward = true,
  isVisibleSkipNext = true,
  portalProps,
  ...props
}: PlayerProps) {
  const { className: audioPropsClassName, ...audioPropsRest } =
    audioProps ?? {};

  const [internalCurrentTime, setInternalCurrentTime] = useState(0);
  const [internalVolume, setInternalVolume] = useState(0);
  const [internalIsPlaying, setInternalIsPlaying] = useState(false);
  const [internalIsFullscreen, setInternalIsFullscreen] = useState(false);

  // external 값이 있으면 그것을 사용, 없으면 internal state 사용
  const currentTime =
    externalCurrentTime !== undefined
      ? externalCurrentTime
      : internalCurrentTime;
  const endTimeText =
    externalEndTimeText !== undefined
      ? externalEndTimeText
      : formatTime(time.end);
  const currentTimeText =
    externalCurrentTimeText !== undefined
      ? externalCurrentTimeText
      : formatTime(currentTime);
  const volume = externalVolume !== undefined ? externalVolume : internalVolume;
  const isPlaying =
    externalIsPlaying !== undefined ? externalIsPlaying : internalIsPlaying;
  const isFullscreen =
    externalIsFullscreen !== undefined
      ? externalIsFullscreen
      : internalIsFullscreen;

  const handleCurrentTimeChange = (newTime: number) => {
    // external 값이 있으면 callback만 호출, 없으면 internal state 업데이트
    if (externalCurrentTime !== undefined) {
      onTimeChange?.(newTime);
    } else {
      setInternalCurrentTime(newTime);
      onTimeChange?.(newTime);
    }
  };

  const handlePlay = () => {
    // external 값이 있으면 callback만 호출, 없으면 internal state 업데이트
    if (externalIsPlaying !== undefined) {
      onPlay?.();
    } else {
      setInternalIsPlaying(true);
      onPlay?.();
    }
  };

  const handlePause = () => {
    // external 값이 있으면 callback만 호출, 없으면 internal state 업데이트
    if (externalIsPlaying !== undefined) {
      onPause?.();
    } else {
      setInternalIsPlaying(false);
      onPause?.();
    }
  };

  const handleVolumeChange = (value: number) => {
    // external 값이 있으면 callback만 호출, 없으면 internal state 업데이트
    if (externalVolume !== undefined) {
      onExternalVolumeChange?.(value);
    } else {
      setInternalVolume(value);
      onExternalVolumeChange?.(value);
    }
  };

  const handleFullscreen = () => {
    const newIsFullscreen = !isFullscreen;
    // external 값이 있으면 callback만 호출, 없으면 internal state 업데이트
    if (externalIsFullscreen !== undefined) {
      onFullscreen?.();
    } else {
      setInternalIsFullscreen(newIsFullscreen);
      onFullscreen?.();
    }
  };

  return (
    <div
      className={cn('flex w-full flex-col select-none', className)}
      {...props}
    >
      {/* Slide 영역 */}
      <div className="flex w-full gap-2xs px-md py-2xs">
        <Text>{currentTimeText ?? formatTime(currentTime)}</Text>
        <Slider
          value={[currentTime]}
          onValueChange={(value) => {
            handleCurrentTimeChange(value[0] ?? 0);
          }}
          min={time.start}
          max={time.end}
          isShowTooltip={true}
          tooltipPosition="top"
          className="w-full"
          tooltipNode={currentTimeText ?? formatTime(currentTime)}
          step={step}
        />
        <Text>{endTimeText ?? formatTime(time.end)}</Text>
      </div>
      {/* video control 영역 */}
      <div className="flex w-full items-center justify-between gap-2xs px-md py-2xs">
        {/* audio 영역 */}
        <div
          className={cn(
            'flex w-full flex-1 items-center justify-start gap-2xs',
          )}
        >
          <div
            className={cn(
              'flex w-full items-center justify-start gap-2xs',
              audioPropsClassName,
            )}
            {...audioPropsRest}
          >
            <div
              className={cn('flex items-center justify-start gap-2xs')}
              onClick={onToggleVolume}
            >
              <ToolButton
                size="sm"
                tooltipNode={volumeTooltip?.node}
                tooltipSubNode={volumeTooltip?.subNode}
                isShowTooltip={
                  !!volumeTooltip?.node || !!volumeTooltip?.subNode
                }
                portalProps={portalProps}
              >
                {volume > 0 && volume < 50 && (
                  <VolumeDown
                    className={
                      'size-[20px] shrink-0 [&_path]:fill-sys-cont-neutral-light'
                    }
                    onClick={() => {
                      handleVolumeChange(0);
                    }}
                  />
                )}
                {volume >= 50 && volume <= 100 && (
                  <VolumeUp
                    className={
                      'size-[20px] shrink-0 [&_path]:fill-sys-cont-neutral-light'
                    }
                  />
                )}
                {volume === 0 && (
                  <VolumeOff
                    className={
                      'size-[20px] shrink-0 [&_path]:fill-sys-cont-neutral-light'
                    }
                    onClick={() => {
                      handleVolumeChange(100);
                    }}
                  />
                )}
              </ToolButton>
            </div>
            <Slider
              value={[volume]}
              onValueChange={(value) => {
                handleVolumeChange(value[0] ?? 0);
              }}
              min={0}
              max={100}
              isShowTooltip={false}
              className="w-[100%]"
            />
          </div>
        </div>
        {/* 재생 영역 */}
        <div className="flex w-full flex-1 justify-center">
          {isVisibleSkipPrevious && (
            <ToolButton
              size="sm"
              tooltipNode={skipPreviousTooltip?.node}
              tooltipSubNode={skipPreviousTooltip?.subNode}
              isShowTooltip={
                !!skipPreviousTooltip?.node || !!skipPreviousTooltip?.subNode
              }
              portalProps={portalProps}
              state={isSkipPreviousDisabled ? 'disabled' : 'default'}
            >
              <SkipPreviousFill
                className={
                  'size-[20px] shrink-0 [&_path]:fill-sys-cont-neutral-default'
                }
                onClick={onSkipPrevious}
              />
            </ToolButton>
          )}

          {isVisibleFastRewind && (
            <ToolButton
              size="sm"
              tooltipNode={fastRewindTooltip?.node}
              tooltipSubNode={fastRewindTooltip?.subNode}
              isShowTooltip={
                !!fastRewindTooltip?.node || !!fastRewindTooltip?.subNode
              }
              portalProps={portalProps}
              state={isFastRewindDisabled ? 'disabled' : 'default'}
            >
              <FastRewindFill
                className={
                  'size-[20px] shrink-0 [&_path]:fill-sys-cont-neutral-default'
                }
                onClick={onFastRewind}
              />
            </ToolButton>
          )}

          <ToolButton
            size="sm"
            tooltipNode={playAndPauseTooltip?.node}
            tooltipSubNode={playAndPauseTooltip?.subNode}
            isShowTooltip={
              !!playAndPauseTooltip?.node || !!playAndPauseTooltip?.subNode
            }
            portalProps={portalProps}
            state={isPlayDisabled ? 'disabled' : 'default'}
          >
            {isPlaying ? (
              <PauseFill
                className={
                  'size-[20px] shrink-0 [&_path]:fill-sys-cont-neutral-default'
                }
                onClick={handlePause}
              />
            ) : (
              <PlayCircleFill
                className={
                  'size-[20px] shrink-0 [&_path]:fill-sys-cont-neutral-default'
                }
                onClick={handlePlay}
              />
            )}
          </ToolButton>

          {isVisibleFastForward && (
            <ToolButton
              size="sm"
              tooltipNode={fastForwardTooltip?.node}
              tooltipSubNode={fastForwardTooltip?.subNode}
              isShowTooltip={
                !!fastForwardTooltip?.node || !!fastForwardTooltip?.subNode
              }
              portalProps={portalProps}
              state={isFastForwardDisabled ? 'disabled' : 'default'}
            >
              <FastForwardFill
                className={
                  'size-[20px] shrink-0 [&_path]:fill-sys-cont-neutral-default'
                }
                onClick={onFastForward}
              />
            </ToolButton>
          )}

          {isVisibleSkipNext && (
            <ToolButton
              size="sm"
              tooltipNode={skipNextTooltip?.node}
              tooltipSubNode={skipNextTooltip?.subNode}
              isShowTooltip={
                !!skipNextTooltip?.node || !!skipNextTooltip?.subNode
              }
              portalProps={portalProps}
              state={isSkipNextDisabled ? 'disabled' : 'default'}
            >
              <SkipNextFill
                className={
                  'size-[20px] shrink-0 [&_path]:fill-sys-cont-neutral-default'
                }
                onClick={onSkipNext}
              />
            </ToolButton>
          )}
        </div>
        {/* 기능 영역 */}
        <div className="flex w-full flex-1 justify-end gap-4xs">
          {externalIsShowRepeat && (
            <ToolButton
              size="sm"
              tooltipNode={repeatTooltip?.node}
              tooltipSubNode={repeatTooltip?.subNode}
              isShowTooltip={!!repeatTooltip?.node || !!repeatTooltip?.subNode}
              onClick={onRepeat}
              portalProps={portalProps}
            >
              <Repeat
                className={
                  'size-[20px] shrink-0 [&_path]:fill-sys-cont-neutral-default'
                }
              />
            </ToolButton>
          )}
          {externalIsShowFullscreen && (
            <ToolButton
              size="sm"
              tooltipNode={fullscreenTooltip?.node}
              tooltipSubNode={fullscreenTooltip?.subNode}
              isShowTooltip={
                !!fullscreenTooltip?.node || !!fullscreenTooltip?.subNode
              }
              onClick={handleFullscreen}
              portalProps={portalProps}
            >
              {isFullscreen ? (
                <FullscreenExit
                  className={
                    'size-[20px] shrink-0 [&_path]:fill-sys-cont-neutral-default'
                  }
                />
              ) : (
                <Fullscreen
                  className={
                    'size-[20px] shrink-0 [&_path]:fill-sys-cont-neutral-default'
                  }
                />
              )}
            </ToolButton>
          )}
        </div>
      </div>
    </div>
  );
}

export default Player;

const Text = ({ children }: { children: React.ReactNode }) => {
  return (
    <p className="text-comp-moderate-small text-sys-cont-neutral-default">
      {children}
    </p>
  );
};
